<script lang="ts">
  import { onMount } from 'svelte';
  import QRCode from 'qrcode';
  import { api } from '../../lib/api/client';
  import type { User } from '../../lib/stores/auth';
  import { currentUser } from '../../lib/stores/auth';
  import { confirm } from '../../lib/stores/confirmDialog';
  import PageHeader from '../../lib/components/PageHeader.svelte';
  import Modal from '../../lib/components/Modal.svelte';
  import type { AreaChoice, RoadData } from '../../lib/api/types';
  import { age } from '../../lib/utils/time';
  import { decimal, integer, locale, t } from '../../lib/i18n/index.ts';

  interface AdminUser extends User {
    disabled: boolean;
    created_at: string;
  }
  interface Settings {
    invites_account_creation: 'group_admins' | 'server_admins';
    group_creation: 'anyone' | 'server_admins';
    detailed_days: number;
    routes_days: number;
    timeline_days: number;
    health_days: number;
    route_detail_m: number;
    driving_speeding_rule: 'over_80' | 'posted';
    driving_limit_source: string;
    routing_fetch: boolean;
    forwarding_enabled: boolean;
    forwarding_private: boolean;
  }

  // part is the topic shown: the server's settings and road data, or its
  // accounts and invites (spec 010 FR-077).
  let { part }: { part: 'server' | 'accounts' } = $props();

  let users: AdminUser[] = $state([]);
  let settings: Settings | null = $state(null);
  let error = $state('');
  let saved = $state(false);
  let showCreate = $state(false);
  let roads = $state<RoadData | null>(null);
  // Adding an area (spec 003 FR-032): the list is read once, when first opened.
  let adding = $state(false);
  let choices: AreaChoice[] | null = $state(null);
  let choicesError = $state('');
  let query = $state('');
  let form = $state({ username: '', display_name: '', password: '', phone: '', email: '', admin: false });
  // Server invites (spec 001): pending ones, and the one just made, shown once.
  interface ServerInvite {
    id: string;
    created_by_name: string;
    created_at: string;
    expires_at: string;
  }
  let invites: ServerInvite[] = $state([]);
  let newInvite: { url: string } | null = $state(null);
  let inviteQR = $state('');
  let copied = $state(false);

  onMount(() => {
    load();
    loadRoads();
    // Fetching and building take minutes; the state is read again while they run.
    const timer = setInterval(() => { if (roads && (roads.state === 'fetching' || roads.state === 'building')) loadRoads(); }, 10_000);
    return () => clearInterval(timer);
  });

  async function loadRoads() {
    try {
      roads = await api.get<RoadData>('/admin/routing');
    } catch (e: any) {
      error = e.message;
    }
  }

  async function keepArea(id: string, kept: boolean) {
    await api.put(`/admin/routing/area?id=${encodeURIComponent(id)}`, { kept }).catch((e: any) => (error = e.message));
    loadRoads();
  }

  async function openAdd() {
    adding = true;
    query = '';
    if (choices) return;
    choicesError = '';
    try {
      choices = await api.get<AreaChoice[]>('/admin/routing/choices');
    } catch (e: any) {
      choicesError = e.message;
    }
  }

  async function addArea(id: string) {
    try {
      await api.post('/admin/routing/areas', { id });
      adding = false;
    } catch (e: any) {
      choicesError = e.message;
    }
    loadRoads();
  }

  let held = $derived(new Set(roads?.areas.map((a) => a.id) ?? []));
  let matches = $derived.by(() => {
    const q = query.trim().toLowerCase();
    return (choices ?? []).filter((c) => !q || c.name.toLowerCase().includes(q) || c.region.toLowerCase().includes(q) || c.id.includes(q));
  });

  async function dropArea(id: string, name: string) {
    if (!(await confirm(t('settingsDropAreaConfirm', { area: name })))) return;
    await api.delete(`/admin/routing/area?id=${encodeURIComponent(id)}`).catch((e: any) => (error = e.message));
    loadRoads();
  }

  const roadState: Record<string, string> = {
    off: t('settingsRoadOff'), empty: t('settingsRoadEmpty'), fetching: t('settingsRoadFetching'), building: t('settingsRoadBuilding'),
    ready: t('settingsRoadReady'),
  };

  function size(bytes: number): string {
    return bytes >= 1 << 30 ? t('settingsSizeGb', { n: decimal(bytes / (1 << 30), 1) }) : t('settingsSizeMb', { n: integer(Math.round(bytes / (1 << 20))) });
  }

  // madeFormat and dayFormat write an invite's times in the numeric forms the
  // browser writes by default.
  const madeFormat = new Intl.DateTimeFormat(locale(), { year: 'numeric', month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit', second: '2-digit' });
  const dayFormat = new Intl.DateTimeFormat(locale());
  const made = (iso: string) => madeFormat.format(new Date(iso)).replace(/\u202f/g, ' ');

  async function load() {
    try {
      const page = await api.get<{ data: AdminUser[] }>('/admin/users?limit=200');
      users = page.data;
      settings = await api.get<Settings>('/admin/settings');
      invites = await api.get<ServerInvite[]>('/admin/invites');
    } catch (e: any) {
      error = e.message;
    }
  }

  async function invite() {
    try {
      newInvite = await api.post<{ url: string }>('/admin/invites');
      copied = false;
      // The QR code holds the same web invite link, which Kehai's scanner and
      // a phone's camera both open.
      inviteQR = await QRCode.toDataURL(newInvite.url, { margin: 1, width: 220 });
      invites = await api.get<ServerInvite[]>('/admin/invites');
    } catch (e: any) {
      error = e.message;
    }
  }

  async function copyInvite() {
    if (!newInvite) return;
    await navigator.clipboard.writeText(newInvite.url).catch(() => {});
    copied = true;
  }

  async function revokeInvite(inv: ServerInvite) {
    if (!(await confirm(t('settingsRevokeConfirm')))) return;
    try {
      await api.delete(`/admin/invites/${inv.id}`);
      invites = invites.filter((i) => i.id !== inv.id);
    } catch (e: any) {
      error = e.message;
    }
  }

  // saveSettings sends the server's settings when one changes, a number or
  // address once its field is left (010 FR-080). A value the server refuses,
  // such as a retention tier shorter than the one before it, stays as typed
  // with the reason.
  async function saveSettings() {
    saved = false;
    try {
      settings = await api.put<Settings>('/admin/settings', settings);
      saved = true;
      error = '';
    } catch (e: any) {
      error = e.message;
    }
  }

  async function deleteTimelines() {
    if (!(await confirm(t('settingsDeleteTimelinesConfirm')))) return;
    try {
      await api.delete('/admin/timeline?confirm=delete');
      error = '';
    } catch (e: any) {
      error = e.message;
    }
  }

  async function createUser() {
    try {
      await api.post('/admin/users', form);
      showCreate = false;
      form = { username: '', display_name: '', password: '', phone: '', email: '', admin: false };
      await load();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function patch(u: AdminUser, body: Record<string, unknown>) {
    try {
      await api.patch(`/admin/users/${u.id}`, body);
      await load();
    } catch (e: any) {
      error = e.message;
    }
  }

  async function resetPassword(u: AdminUser) {
    const pw = prompt(t('settingsResetPasswordPrompt', { user: u.username }));
    if (pw) await patch(u, { password: pw });
  }

  async function remove(u: AdminUser) {
    if (!(await confirm(t('settingsDeleteUserConfirm', { user: u.username })))) return;
    await api.delete(`/admin/users/${u.id}`).catch((e: any) => (error = e.message));
    await load();
  }
</script>

{#if error}
  <div class="mb-4 p-3 rounded text-sm" style="background-color: var(--theme-error); color: var(--theme-base);">{error}</div>
{/if}

{#if part === 'server'}
{#if settings}
  <div class="card p-5 mb-6">
    <PageHeader title={t('settingsServerSettings')} level="section" />
    <div class="flex flex-col gap-4 max-w-md">
      <div>
        <label for="acct" class="block text-sm font-medium mb-1">{t('settingsWhoInvites')}</label>
        <select id="acct" class="input w-full" bind:value={settings.invites_account_creation} onchange={saveSettings}>
          <option value="group_admins">{t('settingsAnyGroupAdmin')}</option>
          <option value="server_admins">{t('settingsServerAdminsOnly')}</option>
        </select>
        <p class="text-xs text-text-muted mt-1">{t('settingsWhoInvitesNote')}</p>
      </div>
      <div>
        <label for="groupcreate" class="block text-sm font-medium mb-1">{t('settingsWhoCreates')}</label>
        <select id="groupcreate" class="input w-full" bind:value={settings.group_creation} onchange={saveSettings}>
          <option value="anyone">{t('settingsAnyone')}</option>
          <option value="server_admins">{t('settingsServerAdminsOnly')}</option>
        </select>
        <p class="text-xs text-text-muted mt-1">{t('settingsWhoCreatesNote')}</p>
      </div>
      <!-- The three retention tiers, each at least as long as the one before (spec 012 FR-035). -->
      <div>
        <label for="detailed" class="block text-sm font-medium mb-1">{t('settingsDetailedDays')}</label>
        <input id="detailed" type="number" min="1" max="3650" class="input w-32" bind:value={settings.detailed_days}
               oninput={() => (saved = false)} onchange={saveSettings} />
        <p class="text-xs text-text-muted mt-1">{t('settingsDetailedDaysNote')}</p>
      </div>
      <div>
        <label for="routes" class="block text-sm font-medium mb-1">{t('settingsRoutesDays')}</label>
        <input id="routes" type="number" min="1" max="36500" class="input w-32" bind:value={settings.routes_days}
               oninput={() => (saved = false)} onchange={saveSettings} />
        <p class="text-xs text-text-muted mt-1">
          {t('settingsRoutesDaysNote')}
        </p>
      </div>
      <div>
        <label for="detail" class="block text-sm font-medium mb-1">{t('settingsRouteDetail')}</label>
        <input id="detail" type="number" min="5" max="500" class="input w-32" bind:value={settings.route_detail_m}
               oninput={() => (saved = false)} onchange={saveSettings} />
        <p class="text-xs text-text-muted mt-1">{t('settingsRouteDetailNote')}</p>
      </div>
      <div>
        <label for="timeline" class="block text-sm font-medium mb-1">{t('settingsTimelineDays')}</label>
        <input id="timeline" type="number" min="0" max="36500" class="input w-32" bind:value={settings.timeline_days}
               oninput={() => (saved = false)} onchange={saveSettings} />
        <p class="text-xs text-text-muted mt-1">
          {t('settingsTimelineDaysNote')}
        </p>
      </div>
      <div>
        <label for="health" class="block text-sm font-medium mb-1">{t('settingsHealthDays')}</label>
        <input id="health" type="number" min="0" max="36500" class="input w-32" bind:value={settings.health_days}
               oninput={() => (saved = false)} onchange={saveSettings} />
        <p class="text-xs text-text-muted mt-1">
          {t('settingsHealthDaysNote')}
        </p>
      </div>
      <fieldset>
        <legend class="block text-sm font-medium mb-1">{t('settingsSpeeding')}</legend>
        <label class="flex items-center gap-2 text-sm">
          <input type="radio" name="speeding" value="over_80" bind:group={settings.driving_speeding_rule} onchange={saveSettings} />
          {t('settingsOver80')}
        </label>
        <label class="flex items-center gap-2 text-sm">
          <input type="radio" name="speeding" value="posted" bind:group={settings.driving_speeding_rule} onchange={saveSettings} />
          {t('settingsOverPosted')}
        </label>
        <p class="text-xs text-text-muted mt-1">
          {t('settingsSpeedingNote')}
        </p>
      </fieldset>
      {#if settings.driving_speeding_rule === 'posted'}
        <div>
          <label for="limits" class="block text-sm font-medium mb-1">{t('settingsLimitSource')}</label>
          <input id="limits" type="url" class="input w-full" bind:value={settings.driving_limit_source} oninput={() => (saved = false)} onchange={saveSettings} />
        </div>
      {/if}
      <label class="flex items-start gap-2 text-sm">
        <input type="checkbox" class="mt-1" bind:checked={settings.routing_fetch} onchange={saveSettings} />
        <span>
          {t('settingsRoutingFetch')}
          <span class="block text-xs text-text-muted">
            {t('settingsRoutingFetchNote')}
          </span>
        </span>
      </label>
      <label class="flex items-start gap-2 text-sm">
        <input type="checkbox" class="mt-1" bind:checked={settings.forwarding_enabled} onchange={saveSettings} />
        <span>
          {t('settingsAllowForwarding')}
          <span class="block text-xs text-text-muted">
            {t('settingsAllowForwardingNote')}
          </span>
        </span>
      </label>
      {#if settings.forwarding_enabled}
        <label class="flex items-start gap-2 text-sm ml-6">
          <input type="checkbox" class="mt-1" bind:checked={settings.forwarding_private} onchange={saveSettings} />
          <span>
            {t('settingsAllowPrivate')}
            <span class="block text-xs text-text-muted">
              {t('settingsAllowPrivateNote')}
            </span>
          </span>
        </label>
      {/if}
      {#if saved}<p class="text-sm" style="color: var(--theme-success);" aria-live="polite">{t('settingsServerSaved')}</p>{/if}
      <div class="flex gap-2">
        <button class="btn btn-danger" type="button" onclick={deleteTimelines}>{t('settingsDeleteAllTimelines')}</button>
      </div>
    </div>
  </div>
{/if}

{#if roads}
  <div class="card p-5 mb-4">
    <PageHeader title={t('settingsRoadData')} level="section">
      <button class="btn btn-secondary btn-sm" onclick={openAdd}><i aria-hidden="true" class="las la-plus mr-1"></i>{t('settingsAddAreaButton')}</button>
    </PageHeader>
    <p class="text-sm mb-1">{roadState[roads.state] ?? roads.state}</p>
    <p class="text-xs text-text-muted mb-3">
      {t('settingsRoadAbout')}
    </p>
    {#if roads.areas.length > 0}
      <ul>
        {#each roads.areas as a (a.id)}
          <li class="py-2 flex items-center gap-3">
            <span class="flex-1 min-w-0">
              <span class="font-medium">{a.name}</span>
              <span class="block text-xs text-text-muted">
                {#if a.fetched_at}{@const line = { size: size(a.bytes), downloaded: age(a.fetched_at, Date.now()), used: age(a.last_used_at, Date.now()) }}{a.roads_at ? t('settingsAreaLine', line) : t('settingsAreaLineUnbuilt', line)}{:else}{t('settingsAreaWaiting')}{/if}
              </span>
            </span>
            <label class="text-sm flex items-center gap-1"><input type="checkbox" checked={a.kept} onchange={(e) => keepArea(a.id, (e.target as HTMLInputElement).checked)} />{t('settingsKeep')}</label>
            <button class="btn btn-secondary btn-sm" onclick={() => dropArea(a.id, a.name)} aria-label={t('settingsDropArea', { area: a.name })}><i aria-hidden="true" class="las la-trash"></i></button>
          </li>
        {/each}
      </ul>
    {/if}
  </div>
{/if}

{/if}

{#if part === 'accounts'}
<div class="card p-5">
  <PageHeader title={t('settingsAccounts')} level="section">
    <div class="flex gap-2">
      <button class="btn btn-secondary btn-sm" onclick={invite}><i aria-hidden="true" class="las la-qrcode mr-1"></i>{t('settingsInvite')}</button>
      <button class="btn btn-secondary btn-sm" onclick={() => (showCreate = true)}><i aria-hidden="true" class="las la-user-plus mr-1"></i>{t('settingsNewAccount')}</button>
    </div>
  </PageHeader>
  <p class="text-xs text-text-muted mb-3">{t('settingsAccountsNote')}</p>
  <ul>
    {#each users as u (u.id)}
      <li class="py-2 flex items-center gap-3">
        <span class="flex-1">
          <span class="font-medium">{u.display_name}</span>
          <span class="text-xs text-text-muted ml-2">@{u.username}</span>
          {#if u.admin}<span class="text-xs ml-2 px-2 py-0.5 rounded bg-surface1">{t('settingsServerAdmin')}</span>{/if}
          {#if u.disabled}<span class="text-xs ml-2 px-2 py-0.5 rounded" style="background: var(--theme-warning); color: var(--theme-base);">{t('settingsDisabled')}</span>{/if}
        </span>
        {#if u.id !== $currentUser?.id}
          <button class="btn btn-secondary btn-sm" onclick={() => patch(u, { admin: !u.admin })}>{u.admin ? t('settingsRemoveAdmin') : t('settingsMakeAdmin')}</button>
          <button class="btn btn-secondary btn-sm" onclick={() => patch(u, { disabled: !u.disabled })}>{u.disabled ? t('settingsEnable') : t('settingsDisable')}</button>
          <button class="btn btn-secondary btn-sm" onclick={() => resetPassword(u)}>{t('settingsResetPassword')}</button>
          <button class="btn btn-secondary btn-sm" onclick={() => remove(u)} aria-label={t('settingsDeleteUser', { user: u.username })}><i aria-hidden="true" class="las la-trash"></i></button>
        {/if}
      </li>
    {/each}
  </ul>
</div>

{#if invites.length > 0}
  <div class="card p-5 mt-6">
    <PageHeader title={t('settingsPendingInvites')} level="section" />
    <ul class="text-sm">
      {#each invites as inv (inv.id)}
        <li class="py-2 flex items-center gap-3">
          <span class="flex-1">{inv.created_by_name
            ? t('settingsInviteBy', { admin: inv.created_by_name, created: made(inv.created_at), expires: dayFormat.format(new Date(inv.expires_at)) })
            : t('settingsInviteByFormer', { created: made(inv.created_at), expires: dayFormat.format(new Date(inv.expires_at)) })}</span>
          <button class="btn btn-secondary btn-sm" onclick={() => revokeInvite(inv)}>{t('settingsRevoke')}</button>
        </li>
      {/each}
    </ul>
  </div>
{/if}

{#if newInvite}
  <Modal title={t('settingsInviteTitle')} onclose={() => (newInvite = null)}>
    <p class="text-sm">{t('settingsInviteAbout')}</p>
    {#if inviteQR}
      <img src={inviteQR} alt={t('settingsInviteQr')} class="rounded-md bg-white p-2 mx-auto" width="220" height="220" />
    {/if}
    <input class="input w-full text-xs" readonly value={newInvite.url} onfocus={(e) => (e.target as HTMLInputElement).select()} />
    <div class="flex justify-end gap-2">
      <button class="btn btn-primary" onclick={copyInvite}>{copied ? t('appCopied') : t('settingsCopyLink')}</button>
    </div>
  </Modal>
{/if}

{/if}

{#if part === 'server'}
{#if adding}
  <Modal title={t('settingsAddArea')} onclose={() => (adding = false)} maxHeight="80vh">
    <p class="text-sm">{t('settingsAddAreaAbout')}</p>
    <input class="input w-full" type="search" placeholder={t('settingsSearchAreasHint')} aria-label={t('settingsSearchAreas')} bind:value={query} />
    {#if choicesError}
      <p class="text-sm" style="color: var(--theme-error);">{choicesError}</p>
    {/if}
    {#if !choices && !choicesError}
      <p class="text-sm text-text-muted">{t('settingsReadingAreas')}</p>
    {:else if choices}
      <ul class="overflow-y-auto min-h-0">
        {#each matches as c (c.id)}
          <li>
            <button class="w-full text-left px-2 py-2 rounded hover:bg-surface0 disabled:opacity-60 disabled:hover:bg-transparent" disabled={held.has(c.id)} onclick={() => addArea(c.id)}>
              <span class="block">{c.name}</span>
              <span class="block text-xs text-text-muted">{held.has(c.id) ? t('settingsAreaHeld') : c.region}</span>
            </button>
          </li>
        {:else}
          <li class="px-2 py-2 text-sm text-text-muted">{t('settingsNoAreaMatches')}</li>
        {/each}
      </ul>
    {/if}
  </Modal>
{/if}

{/if}

{#if part === 'accounts'}
{#if showCreate}
  <Modal title={t('settingsNewAccount')} onclose={() => (showCreate = false)}>
    <form class="flex flex-col gap-3" onsubmit={(e: SubmitEvent) => { e.preventDefault(); createUser(); }}>
      <input class="input" placeholder={t('settingsName')} bind:value={form.display_name} required maxlength="64" />
      <input class="input" placeholder={t('settingsUsername')} bind:value={form.username} required minlength="3" maxlength="32" autocapitalize="none" />
      <input class="input" type="password" placeholder={t('settingsPassword')} bind:value={form.password} required minlength="8" autocomplete="new-password" />
      <input class="input" type="tel" placeholder={t('settingsPhoneOptional')} bind:value={form.phone} maxlength="32" />
      <input class="input" type="email" placeholder={t('settingsEmailOptional')} bind:value={form.email} maxlength="254" />
      <label class="text-sm flex items-center gap-2"><input type="checkbox" bind:checked={form.admin} />{t('settingsServerAdmin')}</label>
      <div class="flex justify-end gap-2">
        <button type="button" class="btn btn-secondary" onclick={() => (showCreate = false)}>{t('appCancel')}</button>
        <button type="submit" class="btn btn-primary">{t('settingsCreate')}</button>
      </div>
    </form>
  </Modal>
{/if}
{/if}
